import { useState, useEffect } from 'react'

export const formatAutoCompleteAddress = (
  addresses: Array<AutoCompleteAddressApi>,
): Array<AutoCompleteAddress> => {
  if (!addresses || addresses.length < 1) return []
  return addresses.map((s) => ({
    placeId: s.place_id,
    description: s.description,
  }))
}
export const IS_SSR = typeof window === 'undefined'
export type AutoCompleteAddressApi = {
  description: string
  place_id: string
}
export type AutoCompleteAddress = {
  description: string
  placeId: string
}

// https://developers.google.com/maps/faq#languagesupport
export const GOOGLE_API_LANGUAGE: Record<string, string> = {
  VN: 'vi',
  ID: 'id',
  TH: 'th',
  MY: 'ms',
  TW: 'zh-TW',
  KH: 'km',
  MM: 'my',
  IN: 'hi',
  PH: 'en',
}
export const GOOGLE_API_KEY =
  process.env.GOOGLE_MAP_KEY || process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY || ''

const googleAutocomplete = async (text = '', countrycode = '') =>
  new Promise((resolve, reject) => {
    if (!text) {
      return reject('Need valid text input')
    }
    if (IS_SSR) {
      return reject('Need valid window object')
    }

    try {
      new window.google.maps.places.AutocompleteService().getPlacePredictions(
        {
          input: text,
          componentRestrictions: { country: countrycode },
          language: GOOGLE_API_LANGUAGE[countrycode],
        },
        resolve,
      )
    } catch (e) {
      reject(e)
    }
  })
const usePlacesAutocomplete = (
  text = '',
  countrycode = '',
  debounceTimeout = 500,
) => {
  const [predictions, setPredictions] = useState<AutoCompleteAddress[]>([])
  const [isLoading, setIsLoading] = useState(false)
  useEffect(() => {
    let handleDebounce: number
    if (text) {
      handleDebounce = window.setTimeout(async () => {
        if (!text) {
          setPredictions([])
          return
        }

        try {
          setIsLoading(true)

          const nextPredictions = await googleAutocomplete(text, countrycode)
          setPredictions(
            formatAutoCompleteAddress(
              nextPredictions as AutoCompleteAddressApi[],
            ),
          )
        } catch (e) {
          console.error(e)
        } finally {
          setIsLoading(false)
        }
      }, debounceTimeout)
    } else if (predictions.length) {
      setPredictions([])
    }

    return () => {
      window.clearTimeout(handleDebounce)
    }
  }, [text, debounceTimeout, countrycode])

  return { isLoading, predictions }
}

export { usePlacesAutocomplete }
